// «Cambia por un kit»: un renglón por kit con el teléfono de la venta, sus piezas extra, precio,
// chip Crédito/Contado, «Ahorras» y «Elegir kit». Mientras cambia, todos los botones esperan.
import { useState } from 'react';
import { Button } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { kitContentsOf } from '../kitContents';
import type { CatalogItem } from '../types';
import { FinancingBadge } from './FinancingBadge';

const NO_DESCRIPTIONS = new Map<string, string>();

const money = (cents: number): string => formatMXN(cents / 100);

function KitRow({
    kit,
    items,
    discountedPriceCents,
    showFinancing,
    swapping,
    disabled,
    onSwap,
}: {
    kit: CatalogItem;
    items: readonly CatalogItem[];
    discountedPriceCents: number | null;
    showFinancing: boolean;
    swapping: boolean;
    disabled: boolean;
    onSwap: () => void;
}) {
    const name = catalogDisplayName(kit, NO_DESCRIPTIONS);
    const contents = kitContentsOf(kit, items, discountedPriceCents);
    // El teléfono ya está en la venta: solo se listan las piezas que se suman.
    const pieces = contents.components.filter((c) => c.category !== 'PHONE');
    const payCents = contents.promoPriceCents ?? contents.kitPriceCents;
    return (
        <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-card px-3 py-2.5 md:grid-cols-[minmax(0,1fr)_104px_84px_112px_136px]">
            <div className="min-w-0">
                <p className="truncate text-[12.5px] font-semibold" title={name}>
                    {name}
                </p>
                <ul className="mt-0.5 border-l border-border pl-2 text-[11px] text-muted-foreground">
                    {pieces.map((piece) => {
                        const label = `+ ${piece.qty > 1 ? `${String(piece.qty)} × ` : ''}${piece.name}`;
                        return (
                            <li key={piece.sku} className="truncate" title={label}>
                                {label}
                            </li>
                        );
                    })}
                </ul>
            </div>
            <div className="text-right text-[12px] tabular-nums">
                {contents.promoPriceCents !== null && (
                    <s className="block text-[10px] text-muted-foreground">
                        {money(contents.kitPriceCents)}
                    </s>
                )}
                <span data-testid="kit-price" className="font-semibold">
                    {money(payCents)}
                </span>
            </div>
            <div className="hidden md:block">
                {showFinancing && kit.financeable !== undefined && (
                    <FinancingBadge financeable={kit.financeable} />
                )}
            </div>
            <div className="hidden text-[11px] md:block">
                {contents.savingsCents !== null && (
                    <>
                        <span className="block text-muted-foreground">Ahorras</span>
                        <span data-testid="kit-savings" className="font-semibold text-success">
                            {money(contents.savingsCents)}
                        </span>
                    </>
                )}
            </div>
            <div className="col-span-2 text-right md:col-span-1">
                <Button
                    size="sm"
                    variant="outline"
                    className="min-w-24"
                    disabled={disabled}
                    isLoading={swapping}
                    aria-label={`Elegir kit ${name}`}
                    onClick={onSwap}
                >
                    {swapping ? 'Cambiando…' : 'Elegir kit'}
                </Button>
            </div>
        </li>
    );
}

export function KitSwapSection({
    kits,
    items,
    showFinancing,
    discountedPriceOf,
    disabled = false,
    onSwap,
}: {
    /** Kits que traen el teléfono de la venta (`kitSwapsFor`). Sin kits no se pinta. */
    kits: readonly CatalogItem[];
    /** Catálogo completo, para resolver las piezas y el ahorro. */
    items: readonly CatalogItem[];
    showFinancing: boolean;
    /** Precio del kit con promo; `null` si ninguna lo baja. */
    discountedPriceOf?: (kit: CatalogItem) => number | null;
    /** Otra escritura de la venta en vuelo. */
    disabled?: boolean;
    onSwap: (sku: string) => Promise<void>;
}) {
    const [swapping, setSwapping] = useState<string | null>(null);
    if (kits.length === 0) return null;

    const swap = (sku: string): void => {
        if (swapping !== null) return;
        setSwapping(sku);
        void onSwap(sku)
            .catch(() => undefined)
            .finally(() => {
                setSwapping(null);
            });
    };

    return (
        <section aria-labelledby="kit-swap-title" className="flex flex-col gap-1.5">
            <h3
                id="kit-swap-title"
                className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
            >
                Cambia por un kit
            </h3>
            <ul className="flex flex-col gap-1.5">
                {kits.map((kit) => (
                    <KitRow
                        key={kit.sku}
                        kit={kit}
                        items={items}
                        discountedPriceCents={discountedPriceOf?.(kit) ?? null}
                        showFinancing={showFinancing}
                        swapping={swapping === kit.sku}
                        disabled={disabled || swapping !== null}
                        onSwap={() => {
                            swap(kit.sku);
                        }}
                    />
                ))}
            </ul>
        </section>
    );
}
